import React, { useEffect, Fragment, useState } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Dialog, Transition } from "@headlessui/react";
import ErrorBoundary from "../../containers/ErrorBoundary";
import { Formik, Form } from "formik";
import SettingsSelect from "../Forms/SettingsSelect";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader, faTimes, faCar } from "@fortawesome/pro-regular-svg-icons";
import { SvgLoader, SvgProxy } from "react-svgmt";
import { getBooking } from "../../store/booking/bookingActions";
import { toast } from "react-toastify";
import NotificationToast from "../Helpers/NotificationToast";

const usePersistentMapHeight = (initialHeight) => {
  const [height, setHeight] = useState(() => {
    const saved = localStorage.getItem("svgMapHeight");
    return saved || initialHeight;
  });

  useEffect(() => {
    localStorage.setItem("svgMapHeight", height);
  }, [height]);

  return [height, setHeight];
};

export default function ModalParkingPlate(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [loadingMap, setLoadingMap] = useState(false);
  const [map, setMap] = useState("");
  const [floors, setFloors] = useState([]);
  const [chosenProperty, setChosenProperty] = useState(
    props.current_booking?.property_id || 0
  );
  const { properties } = useSelector((state) => state.properties);
  const { current_staff } = useSelector((state) => state.auth);
  const [bayInfo, setBayInfo] = useState(null);
  const [svgMapHeight, setSvgMapHeight] = usePersistentMapHeight("80");
  const [occupied, setOccupied] = useState([]);
  const [available, setAvailable] = useState([]);
  const [chosenBay, setChosenBay] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState("");
  const [currentBookingBay, setCurrentBookingBay] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [chosenBayId, setChosenBayId] = useState(0);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [showAddVehicleForm, setShowAddVehicleForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [newVehicle, setNewVehicle] = useState({
    license_plate: "",
    make: "",
    model: "",
    colour: "",
  });
  const [formError, setFormError] = useState("");
  const [sendingAddendum, setSendingAddendum] = useState(false);
  const [pushingVehicle, setPushingVehicle] = useState(null);

  useEffect(() => {
    const checkExistingParkingBay = async () => {
      if (selectedVehicle?.parking_bay_id) {
        const parkingBayId = selectedVehicle.parking_bay_id;
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}get_parking_bay_info/${parkingBayId}`
          );
          const bayInfo = response.data;
          setChosenProperty(bayInfo.property.id);
          setMap(bayInfo.floor.id);
          setBayInfo(bayInfo);
          setSelectedFloor(bayInfo.floor.id);
          setCurrentBookingBay(parkingBayId);
        } catch (error) {
          console.error("Failed to fetch existing parking bay info:", error);
        }
      } else {
        // Reset parking bay related states when switching to a vehicle without a bay
        setCurrentBookingBay(null);
        setBayInfo(null);
        // Keep the current property and floor selection
      }
    };

    checkExistingParkingBay();
  }, [selectedVehicle]);

  // Update the useEffect for initial booking data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      if (props.current_booking?.user?.id) {
        try {
          await fetchUserVehicles(props.current_booking.user.id);
        } catch (error) {
          console.error("Error fetching user vehicles:", error);
        }
      }
    };

    fetchInitialData();
  }, [props.current_booking?.user?.id]);

  // Add zoom controls
  const increaseMapHeight = () => {
    setSvgMapHeight((prevHeight) => Math.min(parseInt(prevHeight) + 10, 150));
  };

  const decreaseMapHeight = () => {
    setSvgMapHeight((prevHeight) => Math.max(parseInt(prevHeight) - 10, 30));
  };

  useEffect(async () => {
    if (chosenProperty > 0) {
      setLoadingMap(true);
      setIsFullyLoaded(false);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}get_property_parking_floors/${chosenProperty}`
        );
        setFloors(response.data.floors);
      } catch (error) {
        console.error("Error fetching floors:", error);
      }
    }

    if (map) {
      setLoadingMap(true);
      setIsFullyLoaded(false);

      try {
        const [occupiedResponse, availableResponse] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}get_floor_parking_status/${map}/occupied`
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL}get_floor_parking_status/${map}/available`
          ),
        ]);

        // Convert the selectors to use parking_bay_id attribute
        const occupiedData = occupiedResponse.data
          ? occupiedResponse.data
              .split(",")
              .map((id) => `[parking_bay_id='${id.replace("#parking_", "")}']`)
              .join(",")
          : "";

        const availableData = availableResponse.data
          ? availableResponse.data
              .split(",")
              .map((id) => `[parking_bay_id='${id.replace("#parking_", "")}']`)
              .join(",")
          : "";

        console.log("Occupied selector:", occupiedData);
        console.log("Available selector:", availableData);

        setOccupied(occupiedData);
        setAvailable(availableData);

        // Log SVG elements to check attributes
        setTimeout(() => {
          const parkingElements = document.querySelectorAll(".mapped");
          console.log(
            "Parking elements:",
            Array.from(parkingElements).map((el) => ({
              id: el.id,
              attributes: Array.from(el.attributes).map((attr) => ({
                name: attr.name,
                value: attr.value,
              })),
            }))
          );
        }, 1000);
      } catch (error) {
        console.error("Error fetching parking status:", error);
      } finally {
        setIsFullyLoaded(true);
        setLoadingMap(false);
      }
    }
  }, [chosenProperty, map]);

  const getBayInfo = async (bayId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}get_parking_bay_info/${bayId}`
      );
      setBayInfo(response.data);
      return true;
    } catch (error) {
      alert("Failed to load parking bay information");
      return false;
    }
  };

  const handleBayClick = (parkingBayId) => {
    // Check if this is an occupied bay
    const isOccupied =
      occupied && occupied.includes(`[parking_bay_id='${parkingBayId}']`);

    // Check if this bay is already assigned to another vehicle in the current booking
    const isAssignedToOtherVehicle = vehicles.some(
      (vehicle) =>
        vehicle.parking_bay_id === parkingBayId &&
        vehicle.id !== selectedVehicle?.id
    );

    if (isOccupied || isAssignedToOtherVehicle) {
      alert(
        "This parking bay is already occupied or assigned to another vehicle"
      );
      return;
    }

    getBayInfo(parkingBayId).then((success) => {
      if (success) {
        setChosenBay(parkingBayId);
      }
    });
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      boxShadow: "none",
      border: state.isFocused ? "1px solid #EE5524" : "1px solid #d1d5db",
      cursor: "pointer",
      "&:hover": {
        border: state.isFocused ? "1px solid #EE5524" : "1px solid #d1d5db",
        cursor: "pointer",
      },
      borderRadius: "10px",
      padding: 0,
    }),
    container: (provided) => ({
      ...provided,
      padding: 0,
      border: 0,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#e7ebe5",
    }),
  };

  const fetchUserVehicles = async (userId) => {
    if (!userId) return;

    try {
      setVehiclesLoading(true);
      // Get vehicles with their parking bay assignments
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}user/${userId}/vehicles?include_parking=true`
      );

      if (response.data && response.data.data) {
        setVehicles(response.data.data);
        console.log("Fetched vehicles:", response.data.data); // Debug log
      } else {
        setVehicles([]);
      }
    } catch (error) {
      console.error("Error fetching user vehicles:", error);
      setVehicles([]);
    } finally {
      setVehiclesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewVehicle({ ...newVehicle, [name]: value });
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!newVehicle.license_plate.trim()) {
      setFormError("License plate is required");
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}user/${props.current_booking.user.id}/vehicles`,
        newVehicle
      );

      // Add the new vehicle to the state
      setVehicles([response.data.data, ...vehicles]);

      // Reset form
      setNewVehicle({
        license_plate: "",
        make: "",
        model: "",
        colour: "",
      });

      setShowAddVehicleForm(false);
    } catch (error) {
      console.error("Error adding vehicle:", error);
      setFormError(error.response?.data?.message || "Failed to add vehicle");
    }
  };

  const handleEditVehicle = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!editingVehicle.license_plate.trim()) {
      setFormError("License plate is required");
      return;
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}vehicle/${editingVehicle.id}`,
        editingVehicle
      );

      // Update the vehicle in the state while preserving parking bay information
      setVehicles(
        vehicles.map((v) => {
          if (v.id === editingVehicle.id) {
            return {
              ...response.data.data,
              parking_bay: v.parking_bay,
              parking_bay_id: v.parking_bay_id,
              addendum_signed: v.addendum_signed,
            };
          }
          return v;
        })
      );

      setEditingVehicle(null);
    } catch (error) {
      console.error("Error updating vehicle:", error);
      setFormError(error.response?.data?.message || "Failed to update vehicle");
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm("Are you sure you want to remove this vehicle?")) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}vehicle/${vehicleId}`
      );

      // Remove the vehicle from state
      setVehicles(vehicles.filter((v) => v.id !== vehicleId));
    } catch (error) {
      console.error("Error removing vehicle:", error);
      alert("Failed to remove vehicle");
    }
  };

  const handleUnassignBay = async (vehicleId) => {
    if (
      !window.confirm("Are you sure you want to unassign this parking bay?")
    ) {
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}unassign_parking_bay`, {
        booking_id: props.current_booking.id,
        vehicle_id: vehicleId,
        staff_id: props.current_staff.id,
      });

      // Update the vehicle in state to remove parking bay and reset addendum_signed
      setVehicles(
        vehicles.map((v) => {
          if (v.id === vehicleId) {
            return {
              ...v,
              parking_bay: null,
              parking_bay_id: null,
              addendum_signed: null, // Reset addendum_signed status
            };
          }
          return v;
        })
      );
    } catch (error) {
      console.error("Error unassigning parking bay:", error);
      alert("Failed to unassign parking bay");
    }
  };

  const assignParkingBay = async (bayId) => {
    setLoadingMap(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}assign_parking_bay`, {
        booking_id: props.current_booking.id,
        parking_bay_id: bayId,
        staff_id: props.current_staff.id,
        vehicle_id: selectedVehicle.id,
      });

      // Update the vehicle in state with new parking bay and reset addendum_signed
      setVehicles(
        vehicles.map((v) => {
          if (v.id === selectedVehicle.id) {
            return {
              ...v,
              parking_bay: bayInfo,
              parking_bay_id: bayId,
              addendum_signed: null, // Reset addendum_signed status
            };
          }
          return v;
        })
      );

      setChosenBay(null);
      setSelectedVehicle(null);
      setShowMap(false);
    } catch (error) {
      console.error("Error assigning parking bay:", error);
      alert("Failed to assign parking bay");
    } finally {
      setLoadingMap(false);
    }
  };

  const handleSendAddendum = async (vehicleId) => {
    if (
      !window.confirm("Are you sure you want to send the parking addendum?")
    ) {
      return;
    }

    setSendingAddendum(vehicleId);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}generate_parking_addendum`,
        {
          booking_id: props.current_booking.id,
          user_id: props.current_booking.user.id,
          vehicle_id: vehicleId,
          format: "url",
          requires_signing: true,
          send_whatsapp: true,
        }
      );

      if (response.data.success === true) {
        // Then send it via WhatsApp
        const sendResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}document/send-whatsapp`,
          {
            file_id: response.data.file?.id,
            user_id: props.current_booking.user.id,
            booking_id: props.current_booking.id,
          }
        );

        if (sendResponse.data.status === "success") {
          toast.success("Addendum sent successfully");
        } else {
          throw new Error(
            sendResponse.data.message || "Failed to send addendum"
          );
        }
      } else {
        throw new Error(response.data.message || "Failed to generate addendum");
      }
    } catch (error) {
      console.error("Error sending addendum:", error);
      toast.error(error.message || "Failed to send addendum");
    } finally {
      setSendingAddendum(null);
    }
  };

  const handlePushVehicleToHik = async (vehicleId) => {
    if (
      !window.confirm(
        "Are you sure you want to push this vehicle to HIK Central?"
      )
    ) {
      return;
    }

    setPushingVehicle(vehicleId);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}push_vehicle_to_hik`,
        {
          booking_id: props.current_booking.id,
          vehicle_id: vehicleId,
        }
      );

      if (response.data.success) {
        toast.success(
          response.data.message || "Vehicle pushed to HIK successfully"
        );
        // Refresh vehicles to get updated hik_vehicle_id
        await fetchUserVehicles(props.current_booking.user.id);
      } else {
        throw new Error(
          response.data.message || "Failed to push vehicle to HIK"
        );
      }
    } catch (error) {
      console.error("Error pushing vehicle to HIK:", error);
      toast.error(
        error.response?.data?.message || "Failed to push vehicle to HIK"
      );
    } finally {
      setPushingVehicle(null);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-20 inset-0 overflow-y-auto"
        open={open}
        onClose={() => dispatch(closeModal())}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            className="inline-block align-middle bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full sm:p-6"
          >
            <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
              <button
                type="button"
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => dispatch(closeModal())}
              >
                <span className="sr-only">Close</span>
                <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
              </button>
            </div>

            <div className="sm:flex sm:items-start w-full">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <Dialog.Title
                  as="h3"
                  className="text-lg leading-6 font-medium text-gray-900 mb-4"
                >
                  {showMap && selectedVehicle
                    ? (() => {
                        const { license_plate, make, model, colour } =
                          selectedVehicle;
                        let details = license_plate || "Vehicle Details";
                        let extra = [make, model].filter(Boolean).join(" ");
                        if (colour)
                          extra = extra ? `${extra}, ${colour}` : colour;
                        return `Parking Bay Assignment - ${details}${
                          extra ? ` (${extra})` : ""
                        }`;
                      })()
                    : "Parking Bay Assignment"}
                </Dialog.Title>

                {!showMap && (
                  <ErrorBoundary>
                    <div className="mb-6">
                      <div className="flex justify-end mb-2">
                        <button
                          onClick={() => setShowAddVehicleForm((prev) => !prev)}
                          className="text-primary hover:text-primary-dark font-medium px-3 py-1 border border-primary rounded-md bg-white"
                        >
                          {showAddVehicleForm ? "Cancel" : "Add Vehicle"}
                        </button>
                      </div>
                      {showAddVehicleForm && (
                        <form
                          onSubmit={handleAddVehicle}
                          className="bg-gray-50 p-4 rounded border mb-4"
                        >
                          <div className="flex space-x-4">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">
                                License Plate *
                              </label>
                              <input
                                type="text"
                                name="license_plate"
                                value={newVehicle.license_plate}
                                onChange={handleInputChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                                required
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Make
                              </label>
                              <input
                                type="text"
                                name="make"
                                value={newVehicle.make}
                                onChange={handleInputChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Model
                              </label>
                              <input
                                type="text"
                                name="model"
                                value={newVehicle.model}
                                onChange={handleInputChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Colour
                              </label>
                              <input
                                type="text"
                                name="colour"
                                value={newVehicle.colour}
                                onChange={handleInputChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              />
                            </div>
                          </div>
                          {formError && (
                            <p className="text-red-500 text-sm mb-2">
                              {formError}
                            </p>
                          )}
                          <button
                            type="submit"
                            className="mt-2 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                          >
                            Add Vehicle
                          </button>
                        </form>
                      )}

                      {editingVehicle && (
                        <form
                          onSubmit={handleEditVehicle}
                          className="bg-gray-50 p-4 rounded border mb-4"
                        >
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium">
                              Edit Vehicle
                            </h3>
                            <button
                              type="button"
                              onClick={() => setEditingVehicle(null)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              <FontAwesomeIcon
                                icon={faTimes}
                                className="h-5 w-5"
                              />
                            </button>
                          </div>
                          <div className="flex space-x-4">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">
                                License Plate *
                              </label>
                              <input
                                type="text"
                                name="license_plate"
                                value={editingVehicle.license_plate}
                                onChange={(e) =>
                                  setEditingVehicle({
                                    ...editingVehicle,
                                    license_plate: e.target.value,
                                  })
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                                required
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Make
                              </label>
                              <input
                                type="text"
                                name="make"
                                value={editingVehicle.make}
                                onChange={(e) =>
                                  setEditingVehicle({
                                    ...editingVehicle,
                                    make: e.target.value,
                                  })
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Model
                              </label>
                              <input
                                type="text"
                                name="model"
                                value={editingVehicle.model}
                                onChange={(e) =>
                                  setEditingVehicle({
                                    ...editingVehicle,
                                    model: e.target.value,
                                  })
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Colour
                              </label>
                              <input
                                type="text"
                                name="colour"
                                value={editingVehicle.colour}
                                onChange={(e) =>
                                  setEditingVehicle({
                                    ...editingVehicle,
                                    colour: e.target.value,
                                  })
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              />
                            </div>
                          </div>
                          {formError && (
                            <p className="text-red-500 text-sm mb-2">
                              {formError}
                            </p>
                          )}
                          <button
                            type="submit"
                            className="mt-2 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                          >
                            Update Vehicle
                          </button>
                        </form>
                      )}

                      {vehiclesLoading ? (
                        <div className="flex items-center justify-center p-4">
                          <FontAwesomeIcon
                            className="w-6 h-6 animate-spin text-primary"
                            icon={faLoader}
                          />
                          <span className="ml-2">Loading vehicles...</span>
                        </div>
                      ) : vehicles.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th
                                  scope="col"
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Vehicle Details
                                </th>
                                <th
                                  scope="col"
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Parking Bay
                                </th>
                                <th
                                  scope="col"
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Status
                                </th>
                                <th
                                  scope="col"
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {vehicles.map((vehicle) => (
                                <tr
                                  key={vehicle.id}
                                  className={
                                    selectedVehicle?.id === vehicle.id
                                      ? "bg-primary bg-opacity-5"
                                      : ""
                                  }
                                >
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {vehicle.license_plate}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {vehicle.make} {vehicle.model}
                                        {vehicle.colour &&
                                          ` - ${vehicle.colour}`}
                                      </p>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {vehicle.parking_bay ? (
                                      <p className="text-sm text-primary">
                                        {vehicle.parking_bay.bay_number}
                                        {vehicle.parking_bay.floor &&
                                          ` (${vehicle.parking_bay.floor.name})`}
                                      </p>
                                    ) : (
                                      <p className="text-sm text-gray-500">
                                        No bay assigned
                                      </p>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {vehicle.parking_bay ? (
                                      vehicle.addendum_signed ? (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                          Addendum Signed
                                          {vehicle.addendum_signed
                                            ? ` (${new Date(
                                                vehicle.addendum_signed
                                              ).toLocaleString()})`
                                            : ""}
                                        </span>
                                      ) : vehicle.requires_addendum === 1 ? (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                          Pending Addendum
                                        </span>
                                      ) : (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                          Parking Bay Assigned
                                        </span>
                                      )
                                    ) : (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                        No Bay Assigned
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                    <button
                                      onClick={() => {
                                        console.log(
                                          "Selected vehicle:",
                                          vehicle
                                        );
                                        setSelectedVehicle(vehicle);
                                        setShowMap(true);
                                        setChosenBayId(0);
                                        setChosenBay(null);
                                      }}
                                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full text-white bg-primary hover:bg-primary_hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    >
                                      {vehicle.parking_bay
                                        ? "Change Bay"
                                        : "Choose Bay"}
                                    </button>
                                    <button
                                      onClick={() => setEditingVehicle(vehicle)}
                                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                    >
                                      Edit
                                    </button>
                                    {!vehicle.parking_bay && (
                                      <button
                                        onClick={() =>
                                          handleDeleteVehicle(vehicle.id)
                                        }
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                      >
                                        Delete Vehicle
                                      </button>
                                    )}
                                    {vehicle.parking_bay && (
                                      <>
                                        <button
                                          onClick={() =>
                                            handleUnassignBay(vehicle.id)
                                          }
                                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                        >
                                          Unassign
                                        </button>
                                        {!vehicle.addendum_signed &&
                                          vehicle.requires_addendum === 1 && (
                                            <button
                                              onClick={() =>
                                                handleSendAddendum(vehicle.id)
                                              }
                                              disabled={
                                                sendingAddendum === vehicle.id
                                              }
                                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            >
                                              {sendingAddendum ===
                                              vehicle.id ? (
                                                <>
                                                  <FontAwesomeIcon
                                                    icon={faLoader}
                                                    className="animate-spin mr-2"
                                                  />
                                                  Sending...
                                                </>
                                              ) : (
                                                "Send Addendum"
                                              )}
                                            </button>
                                          )}
                                      </>
                                    )}
                                    {current_staff?.id === 1 &&
                                      !vehicle.hik_vehicle_id && (
                                        <button
                                          onClick={() =>
                                            handlePushVehicleToHik(vehicle.id)
                                          }
                                          disabled={
                                            pushingVehicle === vehicle.id
                                          }
                                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                          title="Push vehicle to HIK Central"
                                        >
                                          {pushingVehicle === vehicle.id ? (
                                            <>
                                              <FontAwesomeIcon
                                                icon={faLoader}
                                                className="animate-spin mr-2"
                                              />
                                              Pushing...
                                            </>
                                          ) : (
                                            <>
                                              <FontAwesomeIcon
                                                icon={faCar}
                                                className="mr-1"
                                              />
                                              Push to HIK
                                            </>
                                          )}
                                        </button>
                                      )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">
                          No vehicles found for this user.
                        </p>
                      )}
                    </div>
                  </ErrorBoundary>
                )}

                {showMap && selectedVehicle && (
                  <ErrorBoundary>
                    {chosenBay ? (
                      <div className="p-4">
                        <div className="mb-4">
                          <h3 className="text-lg font-medium mb-2">
                            Assign parking bay to{" "}
                            {props.current_booking.user.name}{" "}
                            {props.current_booking.user.surname}?
                          </h3>
                          <div className="mb-4 p-4 bg-gray-50 rounded-md">
                            <p className="mb-1">
                              <span className="font-medium">Bay Number:</span>{" "}
                              {bayInfo.bay_number}
                            </p>
                            {bayInfo.property && (
                              <p className="mb-1">
                                <span className="font-medium">Property:</span>{" "}
                                {bayInfo.property.name}
                              </p>
                            )}
                            {bayInfo.floor && (
                              <p className="mb-1">
                                <span className="font-medium">Floor:</span>{" "}
                                {bayInfo.floor.name}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-center space-x-4">
                          <button
                            className="relative justify-center inline-flex text-white uppercase items-center px-8 py-2 rounded-full border text-sm focus:z-10 focus:outline-none border-primary bg-primary hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5 shadow-md font-bold"
                            onClick={() => assignParkingBay(chosenBay)}
                            disabled={loadingMap}
                          >
                            {loadingMap ? (
                              <>
                                <FontAwesomeIcon
                                  className="w-4 h-4 animate-spin mr-2"
                                  icon={faLoader}
                                />
                                Assigning...
                              </>
                            ) : (
                              "Yes, Assign Bay"
                            )}
                          </button>
                          <button
                            className="relative justify-center inline-flex text-white uppercase items-center px-8 py-2 rounded-full border text-sm focus:z-10 focus:outline-none border-primary bg-primary hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5 shadow-md font-bold"
                            onClick={() => setChosenBay(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <Formik
                        initialValues={{
                          property_id: props.current_booking?.property_id || "",
                          floor_id: selectedFloor || "",
                        }}
                        enableReinitialize={true}
                        onSubmit={async (values) => {
                          setMap(values.floor_id);
                        }}
                      >
                        {({
                          values,
                          setFieldValue,
                          setFieldTouched,
                          handleSubmit,
                        }) => (
                          <Form onSubmit={handleSubmit}>
                            <div className="colour-uc6 rounded-3xl py-7 px-10 flex space-x-4 justify-between items-center mb-4">
                              <div className="flex w-4/5 space-x-4">
                                <div className="w-1/2">
                                  <label className="text-sm font-medium text-gray-700">
                                    Property
                                  </label>
                                  <Select
                                    id="property_id"
                                    name="property_id"
                                    options={properties}
                                    onChange={(val) => {
                                      setFieldValue("property_id", val.value);
                                      setChosenProperty(val.value);
                                      setFieldValue("floor_id", "");
                                    }}
                                    onBlur={setFieldTouched}
                                    value={
                                      properties?.find(
                                        (option) =>
                                          option.value === values.property_id
                                      ) || ""
                                    }
                                    components={{
                                      IndicatorSeparator: () => null,
                                    }}
                                    styles={customStyles}
                                    className="mt-1"
                                  />
                                </div>
                                <div className="w-1/2">
                                  <SettingsSelect
                                    label="FLOOR"
                                    name="floor_id"
                                    required
                                    onChange={setFieldValue}
                                    onBlur={setFieldTouched}
                                    value={values.floor_id}
                                    options={floors}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end space-x-4">
                                <button
                                  type="submit"
                                  className="relative justify-center inline-flex text-white uppercase items-center px-8 py-2 rounded-full border text-sm focus:z-10 focus:outline-none border-primary bg-primary hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5 shadow-md font-bold"
                                >
                                  SEARCH
                                </button>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowMap(false);
                                    setSelectedVehicle(null);
                                  }}
                                  className="relative justify-center inline-flex text-gray-700 uppercase items-center px-8 py-2 rounded-full border text-sm focus:z-10 focus:outline-none border-gray-300 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150 ease-in-ease-out hover:-translate-y-0.5 shadow-md font-bold"
                                >
                                  Back to Vehicles
                                </button>
                              </div>
                            </div>
                            {map && (
                              <div className="relative">
                                {loadingMap && (
                                  <div className="absolute inset-0 bg-white bg-opacity-75 z-50 flex items-center justify-center">
                                    <div className="text-center">
                                      <FontAwesomeIcon
                                        className="w-12 h-12 animate-spin text-primary mb-4"
                                        icon={faLoader}
                                      />
                                      <p className="text-lg font-medium">
                                        Loading parking data...
                                      </p>
                                    </div>
                                  </div>
                                )}
                                {isFullyLoaded && (
                                  <>
                                    <div className="flex justify-between items-center mb-4">
                                      <div className="flex space-x-8">
                                        <div className="flex items-center space-x-2">
                                          <div
                                            className="w-5 h-5 rounded-full"
                                            style={{ backgroundColor: "red" }}
                                          />
                                          <span>Occupied</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <div
                                            className="w-5 h-5 rounded-full"
                                            style={{
                                              backgroundColor: "blue",
                                            }}
                                          />
                                          <span>Current Booking</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <div
                                            className="w-5 h-5 rounded-full"
                                            style={{
                                              backgroundColor: "green",
                                            }}
                                          />
                                          <span>Available</span>
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-4">
                                        <button
                                          type="button"
                                          onClick={decreaseMapHeight}
                                          className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                        >
                                          Zoom Out
                                        </button>
                                        <button
                                          type="button"
                                          onClick={increaseMapHeight}
                                          className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                        >
                                          Zoom In
                                        </button>
                                      </div>
                                    </div>
                                    <div
                                      style={{ height: `${svgMapHeight}vh` }}
                                      className="map_svg"
                                    >
                                      <SvgLoader
                                        path={`${
                                          process.env.REACT_APP_LARAVEL_URL
                                        }api/get_svg/${map}?v=${Math.random()}`}
                                        style={{
                                          width: `${svgMapHeight}vw`,
                                          height: `${svgMapHeight}vh`,
                                        }}
                                      >
                                        <SvgProxy
                                          selector=".mapped"
                                          fill="green"
                                          onClick={(e) => {
                                            const parkingBayId =
                                              e.target.getAttribute(
                                                "parking_bay_id"
                                              );
                                            if (parkingBayId) {
                                              handleBayClick(parkingBayId);
                                            }
                                          }}
                                        />
                                        {occupied && (
                                          <SvgProxy
                                            selector={occupied}
                                            fill="#FF0000"
                                            stroke="#000000"
                                            strokeWidth="1"
                                            onClick={(e) => {
                                              const parkingBayId =
                                                e.target.getAttribute(
                                                  "parking_bay_id"
                                                );
                                              if (parkingBayId) {
                                                alert(
                                                  "This parking bay is already occupied"
                                                );
                                              }
                                            }}
                                          />
                                        )}
                                        {currentBookingBay && (
                                          <SvgProxy
                                            selector={`[parking_bay_id='${currentBookingBay}']`}
                                            fill="blue"
                                            onClick={() => {}}
                                          />
                                        )}
                                      </SvgLoader>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </Form>
                        )}
                      </Formik>
                    )}
                  </ErrorBoundary>
                )}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
